import {
  IMPACT_REPORT_ABOUT_LABEL,
  IMPACT_REPORT_ABOUT_TEXT,
  type IMPACT_REPORT_CERTIFICATION_BADGES,
  IMPACT_REPORT_PHOTO_CREDITS_LABEL,
} from "@/constants/impactReport";
import type { ReportData } from "@/interfaces/impactReport";

interface ImpactReportPdfBackCoverProps {
  /**
   * The credits as the PDF numbers them, printed exactly as handed over. `undefined` or empty
   * prints no credits section, heading included, the same as the on-screen colophon.
   */
  photoCredits: NonNullable<ReportData["thankYou"]>["photoCredits"] | undefined;
  /**
   * The badges with each `src` already a `data:` URI. Required rather than defaulted to the
   * constant: the constant's CDN URLs are unreachable from the converter.
   */
  certificationBadges: typeof IMPACT_REPORT_CERTIFICATION_BADGES;
}

/**
 * The PDF's back cover: the sheet the serialised document prints after the closing page, in
 * place of the on-screen colophon strip. Never rendered on screen.
 *
 * Props only, with no hook and no context, because it is rendered by `renderToStaticMarkup` with
 * no provider mounted. Every string it prints is the colophon's own copy, credits included in the
 * colophon's own format.
 */
export function ImpactReportPdfBackCover({
  photoCredits,
  certificationBadges,
}: ImpactReportPdfBackCoverProps) {
  const hasCredits = photoCredits !== undefined && photoCredits.length > 0;

  return (
    <section className="irv-pdf-back">
      <div className="irv-pdf-back-inner">
        <div>
          <div className="irv-kicker">{IMPACT_REPORT_ABOUT_LABEL}</div>
          <p className="irv-pdf-back-text">{IMPACT_REPORT_ABOUT_TEXT}</p>
        </div>
        {hasCredits && (
          <div>
            <div className="irv-kicker">{IMPACT_REPORT_PHOTO_CREDITS_LABEL}</div>
            <p className="irv-pdf-back-text irv-pdf-back-credits">
              {photoCredits
                .map((credit) => `Page ${credit.page}, ${credit.attribution}`)
                .join("; ")}
            </p>
          </div>
        )}
        <div className="irv-pdf-back-certs">
          {certificationBadges.map((badge) => (
            <img key={badge.alt} className="irv-pdf-back-cert" src={badge.src} alt={badge.alt} />
          ))}
        </div>
      </div>
    </section>
  );
}
